import { formatCount } from '@wit-support/ui';
import { formatMinor } from './format.js';
/*
 * Копия кампании: русские тексты по КОДАМ и ПОЛЯМ ответа auto-ads (K2).
 * auto-ads присылает `message` / `hint` / `why` по-английски (для модели) — на экран они не
 * попадают: человек читает только эти словари. Незнакомый код — общая фраза («не распознано»),
 * английский оригинал виден лишь в подсказке при наведении (`title`), не в тексте экрана.
 */
/** Фраза активации копии — зафиксирована, не выбор (план A2b п. 5). */
export const DUPLICATE_ACTIVATION_TEXT = 'Копия создаётся выключенной — включите её после проверки на странице запуска.';
/** Выключенные в оригинале группы и объявления, взятые в копию, при её включении начнут показываться. */
export const PAUSED_CHILDREN_WARNING = 'Выключенные в оригинале группы и объявления тоже попадут в копию — когда вы включите копию, они начнут показываться и тратить бюджет.';
/**
 * Предупреждение над формой и в итоговом подтверждении: в копию попали выключенные в оригинале
 * группы и объекты. Числа — из `origin.pausedChildren` (работает и для копий нейросети).
 */
export const pausedChildrenWarning = (adSets, ads) => `В копию попали выключенные в оригинале группы (${adSets}) и объявления (${ads}) — при включении копии они тоже начнут показываться и тратить бюджет.`;
/** Предупреждение нужно, только если выключенное действительно попало в копию. */
export function pausedChildrenNote(origin) {
    if (!origin || origin.includePaused !== true || !origin.pausedChildren)
        return null;
    const { adSets, ads } = origin.pausedChildren;
    return adSets + ads > 0 ? pausedChildrenWarning(adSets, ads) : null;
}
/** Копия заведена до обновления: посты и бюджеты групп на сервере не сохранены. */
export const LEGACY_COPY_TEXT = 'Эта копия подготовлена до обновления — посты и бюджеты групп нужно выбрать заново.';
/**
 * Английская подсказка auto-ads в `title` — только если в ней нет id постов и объектов Meta
 * (длинные цифровые id, `pageId_postId`): id на экран не выводим даже в подсказке.
 */
export const tooltipOf = (original) => original === '' || /\d{6,}|\d+_\d+/.test(original) ? undefined : original;
/** Статус пачки копии (`cancelled | draft | execute`) по-русски; `execute` — запущена, не «завершена». */
export const BATCH_STATUS_TEXT = {
    cancelled: 'отменена',
    draft: 'черновик',
    execute: 'запущена на выполнение',
};
export const batchStatusText = (status) => BATCH_STATUS_TEXT[status] ?? 'статус неизвестен';
/** Строка `changed` про выключенные объекты — её показываем как предупреждение. */
export const isPausedChildrenChange = (field) => field === 'status.children';
const BLOCKER_TEXT = {
    'objective-not-sales': {
        title: 'Цель кампании — не «Продажи»',
        todo: 'Конструктор делает только кампании с целью «Продажи»: копия молча стала бы такой. Скопируйте кампанию в Ads Manager вручную.',
    },
    'no-post': {
        title: 'У объявлений нет поста страницы или пост уже удалён',
        todo: 'Конструктор берёт креативы только из постов. Проверьте объявления в Ads Manager или соберите запуск с нуля из существующих постов.',
    },
    'asset-feed-not-supported': {
        title: 'В кампании гибкие креативы (несколько вариантов текста)',
        todo: 'Конструктор не умеет переносить такие объявления как пост. Скопируйте кампанию в Ads Manager вручную.',
    },
    'instagram-only-post': {
        title: 'Объявления продвигают публикацию Instagram без поста страницы',
        todo: 'Нужен пост страницы. Опубликуйте его на странице или соберите запуск вручную.',
    },
    'groups-with-distinct-creatives': {
        title: 'Группы объявлений с разными креативами',
        todo: 'Копия по группам возможна, только если во всех группах одни и те же посты. Скопируйте нужную группу отдельно или соберите запуск вручную.',
    },
    'funnel-settings-write-disabled': {
        title: 'Запись настроек воронки выключена на сервере',
        todo: 'У оригинала есть настройки воронки, а записать их в копию сейчас нельзя: копия ушла бы на воронку по умолчанию (другой оффер и цена триала). Обратитесь к администратору и повторите.',
    },
    'post-page-mismatch': {
        title: 'Посты со страницы, которой нет в профиле кабинета',
        todo: 'Meta не примет объявление с постом чужой страницы. Исправьте страницу в профиле кабинета или выберите другие посты.',
        action: 'accounts',
    },
    'no-active-ads': {
        title: 'Нет включённой группы объявлений с включённым объявлением',
        todo: 'Включите «Брать и выключенные группы и объявления» и подготовьте копию заново.',
        action: 'includePaused',
    },
    'adset-groups-disabled': {
        title: 'Группы объявлений выключены на сервере',
        todo: 'Без групп не переносятся аудитории, поэтому копия отказана. Обратитесь к администратору и повторите.',
    },
    'account-profile-missing': {
        title: 'У кабинета нет профиля конструктора',
        todo: 'Заполните профиль кабинета (страница, Instagram, пиксель) на экране «Кабинеты» и повторите.',
        action: 'accounts',
    },
};
export const UNKNOWN_BLOCKER = {
    title: 'Эту кампанию нельзя скопировать (причина не распознана)',
    todo: 'Скопируйте кампанию в Ads Manager вручную или покажите это разработчикам.',
};
export const blockerText = (code) => BLOCKER_TEXT[code] ?? UNKNOWN_BLOCKER;
const sets = (n) => n == null
    ? 'Группы объявлений'
    : formatCount(n, ['группа объявлений', 'группы объявлений', 'групп объявлений']);
const ads = (n) => n == null ? 'Объявления' : formatCount(n, ['объявление', 'объявления', 'объявлений']);
const NOT_CARRIED = {
    'paused-adsets-skipped': {
        title: (n) => `${sets(n)} выключены в оригинале — не скопированы`,
        hint: 'Чтобы взять и выключенные, подготовьте копию заново с включённым переключателем.',
        names: true,
    },
    'paused-ads-skipped': {
        title: (n) => `${ads(n)} выключены в оригинале — не скопированы`,
        hint: 'Чтобы взять и выключенные, подготовьте копию заново с включённым переключателем.',
        names: true,
    },
    'post-unchecked': {
        title: () => 'Не удалось проверить, что посты ещё существуют',
        hint: 'Кнопка «Проверить» ниже проверит их ещё раз.',
    },
    'funnel-settings-level': {
        title: () => 'Настройки воронки уровня группы и объявления',
        hint: 'Перенесены только настройки кампании. Остальное задайте на копии после создания.',
    },
    'spend-cap': {
        title: () => 'Лимит расхода кампании',
        hint: 'У копии лимита не будет — при необходимости задайте его в Ads Manager.',
    },
    'stop-time': {
        title: () => 'Дата окончания кампании',
        hint: 'Копия будет работать, пока её не остановят.',
    },
    'end-time': {
        title: (n) => `Дата окончания у ${sets(n)}`,
        hint: 'Копии работают, пока их не остановят.',
        names: true,
    },
    'adset-schedule': {
        title: (n) => `Расписание показов у ${sets(n)}`,
        hint: 'Копии показываются без расписания по часам.',
        names: true,
    },
    'frequency-cap': {
        title: (n) => `Ограничение частоты показов у ${sets(n)}`,
        hint: 'У копий ограничения частоты нет.',
        names: true,
    },
    'ad-with-issues': {
        title: (n) => `${ads(n)} с проблемами в Meta`,
        hint: 'Скопированы как есть — Meta может отклонить копию так же, как оригинал.',
    },
    'value-not-mapped': {
        title: () => 'Значение, у которого нет аналога в конструкторе',
        hint: 'Проверьте поле в форме ниже и задайте значение вручную.',
    },
    'lifetime-budget': {
        title: () => 'Бюджет на весь срок',
        hint: 'Конструктор работает с дневными бюджетами — задайте сумму в день.',
    },
    'audience-not-mapped': {
        title: () => 'Аудитория не привязана к слоту профиля кабинета',
        hint: 'Привяжите аудиторию к слоту в профиле кабинета и проверьте копию.',
    },
    'country-list-not-mapped': {
        title: () => 'Исключённые страны не совпали ни с одним списком',
        hint: 'Создайте список стран и укажите его в копии.',
    },
    'adset-difference-not-mapped': {
        title: () => 'Группы различаются тем, что шаблон не хранит по группам',
        hint: 'Взято значение первой группы — проверьте остальные.',
    },
    'several-adsets': {
        title: () => 'Скопирована только первая группа объявлений',
        hint: 'Группы различаются настройками, которых шаблон не умеет хранить по группам.',
    },
    'group-budgets-changed': {
        title: () => 'Сумма бюджетов групп изменилась',
        hint: 'Бюджеты групп задаются при запуске — проверьте итог в день в блоке «Структура».',
    },
    'group-budgets-not-mapped': {
        title: () => 'Бюджеты групп (ABO)',
        hint: 'Заданы в этом запуске, в блоке «Структура».',
    },
    'group-advantage-overlap': {
        title: () => 'Advantage+ аудитория включена в группе с аудиториями',
        hint: 'Группы могут пересекаться сильнее.',
    },
    'groups-too-many': {
        title: () => 'Групп объявлений больше лимита шаблона',
        hint: 'Перенесены только первые.',
    },
    'copies-capped': {
        title: () => 'Одинаковых групп больше лимита копий',
        hint: 'Число копий урезано до лимита.',
    },
    'no-adset': {
        title: () => 'В кампании нет групп объявлений',
        hint: 'Таргетинг не перенесён — задайте его в шаблоне.',
    },
    'bid-ladder-mapped': {
        title: () => 'Лесенка ставок перенесена как шаг ставки и копии',
        hint: 'Проверьте шаг и число копий в блоке «Структура».',
    },
    'gender-all': {
        title: () => 'В оригинале выбраны оба пола',
        hint: 'В шаблоне пол «Все».',
    },
    'adset-deleted-skipped': {
        title: () => 'Удалённые и архивные группы не перенесены',
        hint: 'Так и задумано: копия берёт только живые группы.',
    },
    'adsets-identical': {
        title: () => 'Одинаковые группы перенесены как копии',
        hint: 'Число копий — в блоке «Структура».',
    },
    'adsets-identical-creatives': {
        title: () => 'Группы с одинаковыми настройками и разными постами',
        hint: 'Для каждого поста создаётся своя группа.',
    },
};
const GENERIC_NOT_CARRIED = {
    title: () => 'Перенесено неточно',
    hint: 'Проверьте форму ниже: часть настроек могла не совпасть с оригиналом.',
};
const firstNumber = (s) => {
    const m = /\b(\d+)\b/.exec(s);
    return m ? Number(m[1]) : null;
};
/**
 * Перечень имён после первого «: » подсказки auto-ads («… not copied: A, B … (+3)») → русский хвост.
 * Если у объекта нет имени, auto-ads подставляет его id — голые id (10+ цифр) из перечня убираются.
 */
const namesTail = (hint) => {
    const i = hint.indexOf(': ');
    if (i < 0)
        return null;
    let tail = hint.slice(i + 2).trim();
    let more = 0;
    const m = /\s*…\s*\(\+(\d+)\)\s*$/.exec(tail);
    if (m) {
        more = Number(m[1]);
        tail = tail.slice(0, m.index);
    }
    const names = tail
        .split(', ')
        .map((t) => t.trim())
        .filter((t) => t !== '' && !/^\d{10,}$/.test(t));
    if (names.length === 0)
        return null;
    return `${names.join(', ')}${more > 0 ? ` и ещё ${more}` : ''}`;
};
export function notCarriedLine(row, index, labelOf) {
    const known = NOT_CARRIED[row.code];
    const t = known ?? GENERIC_NOT_CARRIED;
    const count = row.code === 'spend-cap' || row.code === 'stop-time' ? null : firstNumber(row.hint);
    let hint = t.hint;
    if (row.code === 'group-budgets-changed') {
        const m = /was (\S+)\s*\/\s*will be (\S+)/.exec(row.hint);
        if (m)
            hint = `Было ${m[1]} в день, станет ${m[2]} — бюджеты групп задаются при запуске.`;
    }
    if (!known && row.field) {
        const label = labelOf(row.field);
        if (label)
            hint = `Проверьте поле «${label}» в форме ниже: оно могло не совпасть с оригиналом.`;
    }
    return {
        key: `${row.code}-${index}`,
        title: t.title(count),
        hint,
        names: t.names === true ? namesTail(row.hint) : null,
        original: row.hint,
    };
}
const STATUS_RU = {
    ACTIVE: 'включена',
    PAUSED: 'выключена',
    unknown: 'неизвестно',
};
const structureText = (s) => {
    const m = /(\d+) ad set\(s\) \/ (\d+) ad\(s\)/.exec(s);
    if (!m)
        return null;
    return `${formatCount(Number(m[1]), ['группа', 'группы', 'групп'])} / ${formatCount(Number(m[2]), ['объявление', 'объявления', 'объявлений'])}`;
};
export function changedLine(row, index, ctx) {
    const base = { key: `${row.field}-${index}`, original: row.why };
    switch (row.field) {
        case 'campaign.name':
            return {
                ...base,
                label: 'Название кампании',
                from: row.from || null,
                to: /^builder name/i.test(row.to) ? 'стандартное имя конструктора' : row.to || null,
                why: 'У копии своё название; в конец система всегда дописывает свой маркер запуска.',
            };
        case 'status':
            return {
                ...base,
                label: 'Статус',
                from: STATUS_RU[row.from] ?? null,
                to: STATUS_RU[row.to] ?? 'выключена',
                why: 'Копия создаётся выключенной; включение — отдельный шаг, где показана сумма в день.',
            };
        case 'status.children':
            return {
                ...base,
                label: 'Выключенные группы и объявления',
                from: 'выключены',
                to: 'включатся вместе с копией',
                why: PAUSED_CHILDREN_WARNING,
                warn: true,
            };
        case 'creative.urlTags':
            return {
                ...base,
                label: 'Параметры ссылки (UTM)',
                from: 'как в оригинале',
                to: 'метки конструктора',
                why: 'Конструктор добавляет свои метки в каждое объявление копии — атрибуция точнее.',
            };
        case 'budget.dailyMinor': {
            const f = Number(row.from);
            const t = Number(row.to);
            return {
                ...base,
                label: 'Бюджет в день',
                from: Number.isFinite(f) ? formatMinor(f, ctx.currency) : null,
                to: Number.isFinite(t) ? formatMinor(t, ctx.currency) : null,
                why: 'У живого бюджета оригинала «копейки» — служебные центы запуска; копия получит свои.',
            };
        }
        case 'structure':
            return {
                ...base,
                label: 'Структура',
                from: structureText(row.from),
                to: structureText(row.to),
                why: 'Конструктор пересобирает структуру по своей раскладке (группы × посты).',
            };
        case 'creative.instagramAccount':
            return {
                ...base,
                label: 'Аккаунт Instagram',
                from: null,
                to: 'из профиля кабинета',
                why: 'Копия использует аккаунт Instagram из профиля кабинета.',
            };
        default: {
            if (row.field.startsWith('rules.')) {
                const name = ctx.ruleName(row.field.slice('rules.'.length));
                return {
                    ...base,
                    label: name ? `Правило «${name}»` : 'Правило включения',
                    from: 'копию не включает',
                    to: 'может включить копию',
                    why: 'Правило может включить копию без вашего отдельного «да». Измените имя копии или само правило.',
                };
            }
            return {
                ...base,
                label: ctx.labelOf(row.field) ?? 'Параметр',
                from: null,
                to: null,
                why: 'Изменено при копировании.',
            };
        }
    }
}
export function carriedLines(rows, labelOf) {
    const funnel = rows.filter((r) => r.field.startsWith('funnelSettings.'));
    const out = [];
    for (const [i, r] of rows.entries()) {
        if (r.field.startsWith('funnelSettings.'))
            continue;
        if (r.field === 'items') {
            const n = firstNumber(r.value);
            out.push({
                key: `items-${i}`,
                label: 'Креативы',
                value: n != null ? formatCount(n, ['пост', 'поста', 'постов']) : 'посты объявлений',
            });
        }
        else if (r.field === 'adSetGroups') {
            out.push({ key: `groups-${i}`, label: 'Группы шаблона', value: r.value });
        }
        else if (r.field === 'status') {
            out.push({
                key: `status-${i}`,
                label: 'Статус',
                value: 'выключена — копия создаётся выключенной',
            });
        }
        else {
            out.push({ key: `${r.field}-${i}`, label: labelOf(r.field) ?? 'Параметр', value: r.value });
        }
    }
    if (funnel.length > 0) {
        out.push({
            key: 'funnel',
            label: 'Настройки воронки',
            value: formatCount(funnel.length, ['настройка', 'настройки', 'настроек']),
        });
    }
    return out;
}
// --- правила ------------------------------------------------------------------------------------
const COVERAGE_TEXT = {
    yes: 'Правило будет действовать и на копию',
    maybe: 'Правило может начать действовать на копию (зависит от имени)',
    no: 'На копию правило не действует',
};
/**
 * Строка правила — A/B-привязка оригинала к варианту бюджета: машинное `abVariantKey` у новых
 * копий; у копий до него — `via: 'id'` + copyGets «No A/B budget variant…» (запасной признак).
 */
const isBudgetVariantBinding = (r) => r.abVariantKey !== undefined ||
    (r.via === 'id' && /^No A\/B budget variant/.test(r.copyGets ?? ''));
/**
 * Что получит копия, словами. Основа — машинные поля auto-ads (`coverage`, `mayEnableCopy`); если
 * их нет (старый ответ), смысл определяется по характерным оборотам английской фразы `copyGets`,
 * нераспознанное — общая фраза (оригинал — в подсказку).
 */
export function ruleCopyText(r) {
    if (r.coverage) {
        const base = `${COVERAGE_TEXT[r.coverage]}${r.mayEnableCopy === true ? ' — это правило может включить копию' : ''}.`;
        // привязка оригинала к A/B-варианту бюджета: вариант копия не наследует
        if (!isBudgetVariantBinding(r))
            return base;
        return r.abVariantKey === undefined
            ? `${base} Копия не получит вариант A/B этого правила.`
            : `${base} Копия не получит вариант A/B «${r.abVariantKey}» этого правила.`;
    }
    const t = r.copyGets;
    if (r.abVariantKey !== undefined || /No A\/B budget variant/i.test(t)) {
        return 'Оригинал входит в A/B-вариант бюджета; копия варианта не получит, пока правило само не назначит.';
    }
    if (/applies to every campaign|WILL cover/.test(t))
        return 'Правило будет действовать и на копию.';
    if (/may cover|not expected to cover/i.test(t)) {
        return /not expected/i.test(t)
            ? 'Скорее всего, правило копию не затронет.'
            : 'Правило, возможно, будет действовать и на копию — точное имя копии станет известно при запуске.';
    }
    if (/not covered|not in the rule|Not bound|not in the list/i.test(t)) {
        return 'Копию это правило не затрагивает.';
    }
    return 'Что получит копия, зависит от области действия правила — загляните в правило.';
}
export const ruleBindingText = (b) => b === 'excluded' ? 'Оригинал исключён из правила' : 'Оригинал входит в правило';
//# sourceMappingURL=duplicate-texts.js.map